import { useMemo } from 'react';
import type { PresenceStatus } from '@/types/domain';
import { resolveMediaUrl } from '@/utils/media';
import styles from './Avatar.module.css';

export interface AvatarProps {
  src?: string | null;
  name: string;
  size?: number;
  presence?: PresenceStatus;
}

function initials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
}

/**
 * F-160 D3 — djb2 hash → HSL hue so empty avatars get a stable, name-derived
 * colour instead of the dull universal grey. Same name always → same hue;
 * keeps `s/l` fixed so the contrast vs white initials stays predictable.
 */
function hashToHue(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) + h + s.charCodeAt(i);
  }
  return Math.abs(h) % 360;
}

/**
 * F-180 A7 — WCAG AA fix. Yellow/green hues at H=50..90 are perceptually
 * lighter than their L=32% suggests and tank white-text contrast below
 * 4.5:1. Shift offending hues by +60° into the orange band where
 * contrast holds. Saturation is capped at 60% to keep tones earthy
 * (neon greens at S=100% still fail even at L=32%).
 */
function safeHue(hue: number): number {
  return hue >= 50 && hue <= 90 ? (hue + 60) % 360 : hue;
}

export function Avatar({ src, name, size = 48, presence }: AvatarProps): JSX.Element {
  const fallbackBg = useMemo(
    () => `hsl(${safeHue(hashToHue(name || 'X'))} 60% 32%)`,
    [name],
  );
  const style = useMemo(
    () => ({
      width: size,
      height: size,
      fontSize: Math.max(12, size * 0.38),
      ...(src ? null : { background: fallbackBg, color: '#fff' }),
    }),
    [size, src, fallbackBg],
  );
  return (
    <span className={styles.root} style={style} aria-label={name}>
      {src ? <img src={resolveMediaUrl(src)} alt={name} className={styles.image} /> : <span>{initials(name)}</span>}
      {presence ? <span className={[styles.presence, styles[presence]].join(' ')} /> : null}
    </span>
  );
}
